import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Droplets, Shield, Zap, ArrowRight, CheckCircle, Beaker, Target, Settings, Layers, Wrench, Factory, AlertCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import UnifiedContactModal from '../components/UnifiedContactModal';

const DPADrenchersPage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const whyDPABenefits = [
    'Reduce superficial scald and peel browning in long-term storage',
    'Help reduce CA-related CO₂ injury risk in sensitive varieties when used with appropriate storage management',
    'Support overall storage quality on high-risk cultivars'
  ];

  const whatIsDPA = [
    'DPA stands for diphenylamine, a postharvest antioxidant.',
    'Commonly used on scald-prone apple varieties going into long-term storage.',
    'Typically applied as a water-based drench to full bins before storage, according to local labels and postharvest recommendations.',
    'One tool among several (along with CA, SmartFresh, fungicides, and sanitation) to help protect storage quality.'
  ];

  const bestSuitedFor = [
    'Scald-prone apple varieties (e.g., Granny Smith, Red Delicious and similar types)',
    'Fruit destined for long-term CA or export programs',
    'Operations that need consistent, documented postharvest treatment'
  ];

  const systemElements = [
    'Truck-through or bin conveyor drench booth with overhead and side spray manifolds',
    'One or more solution tanks sized to your throughput and program needs',
    'Pumps, piping, and agitation to keep chemistry uniform',
    'Collection pit and return piping so solution drains back to the tank',
    'Operator-friendly controls for starting cycles and selecting the correct program'
  ];

  const singleTankFeatures = [
    'One main solution tank serving the drencher',
    'Ideal for smaller sheds or single-program operations',
    'Lower capital cost and simpler plumbing',
    'Good fit when most fruit runs under the same DPA and fungicide program'
  ];

  const multiTankFeatures = [
    'Dedicated tanks and circuits for different chemistries or programs',
    'Helps separate DPA vs non-DPA fruit, export vs domestic, or organic vs conventional',
    'Faster changeovers between varieties and customers',
    'Clear segregation story for auditors and buyers'
  ];

  const hygieneFeatures = [
    'Sloped collection and sumps for efficient solution recovery',
    'Filtration and screening options to manage leaves, debris, and bin slivers',
    'Continuous tank agitation to help keep concentration uniform',
    'Accessible spray manifolds for inspection and cleaning',
    'Drain and rinse provisions to turn tanks and lines over between seasons or programs'
  ];

  const systemOptions = [
    'Truck-through bays or stand-alone bin drench tunnels',
    'Above-ground or in-ground solution tanks',
    'Single-lane or multi-lane drench booths',
    'Integration with existing receiving lines, hydrocoolers, or bin handling systems',
    'Control panels with simple manual operation or PLC-based control'
  ];

  const whyCTCBenefits = [
    'Experience in apple and pear postharvest systems',
    'Custom engineering to fit your layout and throughput',
    'Mixing and non-cross-mixing options matched to your programs',
    'Integration with existing hydrocoolers, filtration, and facility infrastructure',
    'Support from design through installation and startup'
  ];

  const faqs = [
    {
      question: 'Do you specify which DPA or fungicide products we use?',
      answer: 'No. CTC supplies the equipment and system design. Your postharvest advisor, chemical supplier, and the product label determine which products and rates you use in the drencher. Availability and legal status of DPA and specific fungicides can vary by country and market, so you and your advisors are responsible for making sure all products are used according to local regulations and MRL requirements.'
    },
    {
      question: 'Can the same drencher be used for DPA and non-DPA programs?',
      answer: 'Yes. Many facilities run both. Depending on your requirements, we can design a single-tank system with turn-over procedures between programs or a multi-tank non-cross-mixing system with dedicated circuits.'
    },
    {
      question: 'Can you retrofit our existing drench bay?',
      answer: 'In many cases we can. If you already have a concrete bay or partial infrastructure, we can evaluate the site and propose piping, tanks, manifolds, and controls to bring it up to current needs.'
    },
    {
      question: 'How do we get started?',
      answer: 'Start with a conversation about your fruit mix, volumes, existing layout, and postharvest program. From there we can propose a preliminary concept and budget for a system that fits your operation.'
    },
    {
      question: 'Why use a DPA drencher instead of only fogging DPA in storage rooms?',
      answer: 'Fogging systems can apply DPA inside loaded rooms, but they\'re usually limited to specific products and timing. A drencher lets you treat fruit right at receiving and combine DPA with compatible fungicides in the same solution. That gives you: Treatment earlier in the process, before fruit ever hits storage; Uniform coverage on bins and fruit as they pass through the booth; Flexibility to run different recipes by variety or program using mixing and non-cross-mixing tanks.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="DPA Drenchers | Apple & Pear Storage Scald Control | CTC Waterworks"
        description="Custom truck-through DPA drencher systems for apples and pears. Protect high-value fruit from storage scald and decay with diphenylamine treatment systems designed for Northwest packers."
        canonicalPath="/dpa-drenchers"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-green-50 via-sky-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Droplets size={16} />
              <span>Postharvest Scald & Decay Control</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              DPA Drenchers for Apples & Pears
            </h1>
            <p className="text-2xl text-gray-700 max-w-4xl mx-auto mb-4 font-semibold">
              Custom truck-through drench systems to protect high-value fruit from storage scald and decay.
            </p>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto mb-8 leading-relaxed">
              CTC DPA Drenchers are postharvest treatment systems that apply DPA (diphenylamine) and fungicide solutions to apples and pears in bins as they arrive from the field. We design and build truck-through and bin-drench systems that fit your layout, your varieties, and your storage programs.
            </p>
            <motion.button
              onClick={() => setIsContactModalOpen(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-ctc-blue to-ctc-blue-dark text-white px-8 py-4 rounded-lg font-semibold hover:from-ctc-blue-dark hover:to-ctc-blue transition-all duration-300 shadow-lg inline-flex items-center space-x-2"
            >
              <span>Talk to a DPA Systems Specialist</span>
              <ArrowRight size={20} />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Why DPA Drenchers Matter */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-8">
              Why DPA Drenchers Matter
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div className="space-y-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  Every bin going into storage represents a big investment in growing, picking, and hauling fruit. Scald, storage disorders, and decay can erase that value months after harvest.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  DPA (diphenylamine) is a proven antioxidant used on susceptible apple varieties and, in some programs, pears to help:
                </p>
                <ul className="space-y-3">
                  {whyDPABenefits.map((benefit, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-lg text-gray-700 leading-relaxed">
                  A DPA drencher gives you a consistent, controlled way to apply DPA and compatible fungicides to full bins right at the receiving area—before fruit goes into storage.
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-200">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    Best Suited For
                  </h3>
                  <ul className="space-y-4">
                    {bestSuitedFor.map((item, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <Shield className="text-green-600 flex-shrink-0 mt-1" size={20} />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* What Is DPA? Infobox */}
                <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-2xl p-8 border border-blue-200">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    What Is DPA?
                  </h3>
                  <ul className="space-y-3">
                    {whatIsDPA.map((item, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={18} />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How Our DPA Drench Systems Work */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-8">
              How Our DPA Drench Systems Work
            </h2>

            <div className="mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                CTC DPA Drenchers are built around a simple idea: move full bins through a controlled spray tunnel and recapture the treatment solution for reuse.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-8 font-semibold">
                Typical system elements include:
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {systemElements.map((element, index) => (
                <div key={index} className="bg-white rounded-lg p-6 border-2 border-gray-200 hover:border-ctc-blue transition-colors shadow-sm">
                  <div className="flex items-start space-x-3">
                    <Settings className="text-ctc-blue flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">{element}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-sky-50 rounded-xl p-8 border border-sky-200">
              <p className="text-lg text-gray-700 leading-relaxed">
                Bins are loaded on trucks or handling equipment as normal. As they pass through the drencher, spray manifolds deliver a uniform curtain of solution across each stack of bins. Runoff is captured, filtered, and returned to the appropriate tank for reuse until the solution is turned over according to your postharvest program. Some facilities also run low- or non-recycling modes, using fresh solution more frequently as part of their food-safety strategy—CTC can design for that as well.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mixing and Non-Cross-Mixing Options */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Mixing and Non-Cross-Mixing Options
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-12">
              Different facilities manage their chemistry programs in different ways. CTC designs both shared-tank 'mixing' systems and multi-tank non-cross-mixing systems, depending on your varieties, programs, and risk tolerance.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Single-Tank Mixing Systems */}
              <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-xl p-8 border border-blue-200">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-sky-600 rounded-lg flex items-center justify-center">
                    <Beaker className="text-white" size={24} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Single-Tank Mixing Systems
                  </h3>
                </div>
                <ul className="space-y-3">
                  {singleTankFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={18} />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Multi-Tank Non-Cross-Mixing Systems */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-8 border border-green-200">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                    <Layers className="text-white" size={24} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Multi-Tank Non-Cross-Mixing Systems
                  </h3>
                </div>
                <ul className="space-y-3">
                  {multiTankFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={18} />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
              <p className="text-gray-700 leading-relaxed">
                We'll work with your postharvest advisor and chemical suppliers to match the drencher configuration to your actual programs. CTC supplies the hardware and system design; you and your advisors decide which labeled products and rates to use.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Built for Sanitation and Serviceability */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Built for Sanitation and Serviceability
            </h2>

            <div className="mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Recycling drenches can concentrate organic matter and microorganisms over time. That's why our systems are designed with hygiene and clean-out in mind:
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {hygieneFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-lg p-6 border-2 border-gray-200 hover:border-ctc-blue transition-colors shadow-sm">
                  <div className="flex items-start space-x-3">
                    <Wrench className="text-ctc-blue flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-sky-50 rounded-xl p-6 border border-sky-200">
              <p className="text-gray-700 leading-relaxed">
                We design to support your food-safety program and sanitation procedures, not replace them. Our goal is to make it practical for your team to maintain, inspect, and clean the drencher on a routine basis.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* System Sizes and Integration */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              System Sizes and Integration
            </h2>

            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Because every shed layout is different, CTC builds DPA drenchers to fit your space and existing equipment:
            </p>

            <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-2xl p-8 border border-blue-200 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Typical Options Include:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {systemOptions.map((option, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Factory className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">{option}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                We'll start with your throughput targets, available space, and postharvest program, then design a system that fits. Whether you need to upgrade an old concrete bay or add a new drencher at a greenfield site, we can tailor the layout to your operation.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Applications: Apples & Pears First */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Applications: Apples & Pears First
            </h2>

            <div className="bg-white rounded-xl p-8 border border-green-200">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                DPA drencher systems are used primarily on apples and, in some cases, pears that are at risk of storage scald or are going into long-term storage programs.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                While the same hardware can sometimes be used for other crops or chemistries, most CTC DPA Drenchers are designed around:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Fresh apples for domestic and export markets</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Pears in programs where antioxidant or fungicide drenches are part of the postharvest strategy</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                If you have other crops or postharvest treatment needs, we can evaluate whether a similar drench system is a good fit and tailor the design accordingly.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Work with CTC Waterworks */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why Work with CTC Waterworks?
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  CTC Waterworks has been building specialized equipment for Northwest fruit packers and growers for more than 40 years. Our team understands how postharvest equipment fits into real-world sheds.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6 font-semibold">
                  With CTC you get:
                </p>
                <ul className="space-y-4">
                  {whyCTCBenefits.map((benefit, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="text-ctc-blue flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gradient-to-br from-ctc-blue to-ctc-blue-dark rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">
                  Our Focus
                </h3>
                <p className="text-lg opacity-90 leading-relaxed">
                  We focus on rugged, maintainable systems that your team can operate season after season.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* DPA Drencher FAQs */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              DPA Drencher FAQs
            </h2>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertCircle className="text-ctc-blue flex-shrink-0 mt-1" size={20} />
                    <h3 className="text-xl font-bold text-gray-900">
                      {faq.question}
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed ml-8">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-ctc-blue via-sky-600 to-ctc-blue-dark">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Plan Your Next DPA Drench System
            </h2>
            <p className="text-xl text-white opacity-90 mb-8 leading-relaxed">
              Whether you're refreshing an aging drencher or adding DPA capabilities for the first time, we can help you design a system that fits your fruit, programs, and facility.
            </p>
            <motion.button
              onClick={() => setIsContactModalOpen(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center space-x-2 shadow-lg"
            >
              <span>Talk to a DPA Systems Specialist</span>
              <ArrowRight size={20} />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Unified Contact Modal */}
      <UnifiedContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        productType="dpa-drenchers"
        formSource="product_quote"
      />

      <Footer />
    </div>
  );
};

export default DPADrenchersPage;
